import {
  agGridMenuSeparator,
  AppwideDrawerContentType,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  columnTypes,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  ExportMenuItems,
  filterByCellValueMenuItem,
  FIXING_INDEX_MARK,
  FormControlSizes,
  HStack,
  IconName,
  IndexMarkRow,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useAppwideDrawerContext,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  WSBlotterTableMaxRecordsReachedWarning,
  type Column,
  type FixingIndexMarkMessageData,
  type MinimalSubscriptionResponse,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, RowDoubleClickedEvent } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IndexMarksEditDrawer } from './IndexMarksEditDrawer';
import { useIndexMarksColumns } from './useIndexMarksColumns';
import {
  colIDToFilterBuilderKey,
  onlyServerFilterKeys,
  useIndexMarksFilter,
  type IndexMarksFilter,
} from './useIndexMarksFilter';

export interface IndexMarksBlotterTableProps {
  blotterID: string;
  blotterPersistID?: string;
  blotterFiltersPortalID?: string;
  isInDetailsDrawer?: boolean;
  tabLabel?: string;
  defaultColumns?: (Column | keyof FixingIndexMarkMessageData)[];
  defaultFilter?: IndexMarksFilter;
  persistFilter?: boolean;
  persistGridState?: boolean;
  initialIsOpen?: boolean;
  onCloneTab?: () => void;
}

export function IndexMarksBlotterTable({
  blotterID,
  blotterPersistID = blotterID,
  blotterFiltersPortalID,
  isInDetailsDrawer = false,
  tabLabel,
  defaultColumns,
  defaultFilter,
  persistFilter,
  persistGridState,
  initialIsOpen,
  onCloneTab,
}: IndexMarksBlotterTableProps) {
  const mixpanel = useMixpanel();
  const { openDrawer, closeDrawer } = useAppwideDrawerContext();

  const columns = useIndexMarksColumns({ defaultColumns });
  const persistedBlotterTable = usePersistedBlotterTable(
    blotterPersistID,
    { filter: defaultFilter },
    { persistFilter, persistGridState }
  );

  const filterResults = useIndexMarksFilter({
    persistedBlotterTable,
  });
  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps, filter } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });
  const openIndexMarkDrawer = useCallbackRef((rowToEdit?: FixingIndexMarkMessageData) => {
    openDrawer({
      type: AppwideDrawerContentType.IndexMarkEditor,
      title: rowToEdit ? 'Edit Index Mark' : 'New Index Mark',
      width: 520,
      drawerHeaderDesign: 'tab',
      renderContent: () => <IndexMarksEditDrawer initialIndexMark={rowToEdit} onClose={closeDrawer} />,
    });
  });

  const handleRowDoubleClick = useCallbackRef((event: RowDoubleClickedEvent<FixingIndexMarkMessageData>) => {
    if (isInDetailsDrawer || event.data == null) {
      return;
    }

    openIndexMarkDrawer(event.data);
  });

  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<FixingIndexMarkMessageData>) => {
    const rightClickedRow = params.node?.data;
    if (rightClickedRow == null) {
      return [];
    }

    const editMarkMenuItem = !isInDetailsDrawer
      ? {
          name: 'Edit Mark',
          icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
          action: () => openIndexMarkDrawer(rightClickedRow),
        }
      : undefined;

    return compact([
      editMarkMenuItem,
      editMarkMenuItem ? agGridMenuSeparator : undefined,
      ...filterByCellValueMenuItem({
        params,
        filterableProperties: filterBuilderProps.properties,
        openClause: filterBuilderAccordion.openClause,
        colIDToFilterBuilderKey,
        mixpanel,
      }),
    ]);
  });

  const request = useMemo(
    () => ({
      name: FIXING_INDEX_MARK,
      tag: blotterID,
      sort_by: '-Timestamp',
      ...removeEmptyFilters(onlyServerFilterKeys(filter)),
    }),
    [blotterID, filter]
  );

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        editable: false,
        hide: false,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const indexMarkRowPipe = useMemo(
    () => (source: Observable<MinimalSubscriptionResponse<FixingIndexMarkMessageData>>) =>
      source.pipe(
        map(
          (response): MinimalSubscriptionResponse<FixingIndexMarkMessageData> => ({
            ...response,
            data: response.data?.map(row => new IndexMarkRow(row)),
          })
        )
      ),
    []
  );

  const blotterTable = useWsBlotterTable({
    request,
    rowID: 'ID',
    clientLocalFilter,
    persistence: persistedBlotterTable,
    columns,
    contextMenuOptions: {
      getCustomContextMenuItems,
    },
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      rowGroupPanelShow: isInDetailsDrawer ? 'never' : 'always',
      autoGroupColumnDef,
      onRowDoubleClicked: handleRowDoubleClick,
    },
    exportOptions: {
      name: 'IndexMarks',
      tabLabel,
    },
    pipe: indexMarkRowPipe,
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        portalId={blotterFiltersPortalID}
        showFilterBuilder={!isInDetailsDrawer}
        showQuickFilter={true}
        suffix={
          !isInDetailsDrawer && (
            <HStack gap="spacing8">
              <Button
                startIcon={IconName.Plus}
                variant={ButtonVariants.Positive}
                size={FormControlSizes.Small}
                onClick={() => openIndexMarkDrawer()}
                data-testid="index-mark-new-button"
              >
                New Mark
              </Button>
              <BlotterTableExtrasMenu {...extrasMenuPopover}>
                <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
                {onCloneTab && (
                  <Button
                    startIcon={IconName.Duplicate}
                    variant={ButtonVariants.Default}
                    size={FormControlSizes.Small}
                    onClick={handleCloneTab}
                  >
                    Clone Tab
                  </Button>
                )}
              </BlotterTableExtrasMenu>
            </HStack>
          )
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={row => row.Timestamp} />
      <BlotterTable {...blotterTable} />
    </>
  );
}
